import { PLATFORMS } from '@/data/siteData'
import { useModal } from '@/hooks/useModal'
import Modal from '@/components/Modal/Modal'
import PlatformCard from '@/components/PlatformCard/PlatformCard'
import PlatformDetail from '@/components/PlatformDetail/PlatformDetail'
import StatsBand from '@/components/StatsBand/StatsBand'
import CtaSection from '@/components/CtaSection/CtaSection'
import styles from './PlatformPage.module.css'

const platform = PLATFORMS.find(p => p.id === 'skillnexus')

const FEATURES_DETAIL = [
  {
    icon: '📚',
    title: 'Structured Batch Learning',
    desc: 'Enrol in expertly curated batches with day-wise content, ensuring consistent progress and measurable outcomes.',
    bg: 'rgba(108,127,204,0.12)',
  },
  {
    icon: '🤖',
    title: 'AI-Generated Content',
    desc: 'Dynamic course content powered by AI, tailored to your learning pace and updated to reflect current industry standards.',
    bg: 'rgba(108,127,204,0.12)',
  },
  {
    icon: '🎥',
    title: 'Live Mentor Sessions',
    desc: 'One-click access to Microsoft Teams sessions with experienced mentors who guide your learning journey.',
    bg: 'rgba(108,127,204,0.12)',
  },
  {
    icon: '✅',
    title: 'Auto-Graded Assessments',
    desc: 'Real-time quiz feedback and progress dashboards keep you on track and identify areas for improvement.',
    bg: 'rgba(108,127,204,0.12)',
  },
  {
    icon: '📜',
    title: 'Verified Certificates',
    desc: 'LinkedIn-shareable PDF certificates upon completion, recognized by our partner companies.',
    bg: 'rgba(108,127,204,0.12)',
  },
  {
    icon: '📱',
    title: 'Mobile-First PWA',
    desc: 'Learn anywhere with our Progressive Web App — fully optimized for mobile, tablet, and desktop.',
    bg: 'rgba(108,127,204,0.12)',
  },
]

const COURSE_GROUPS = [
  'Data Science', 'Machine Learning', 'Web Development', 'AI/ML Engineering',
  'Cybersecurity', 'Cloud Computing', 'DevOps', 'UI/UX Design',
  'Business Analytics', 'Digital Marketing', 'Python Programming', 'Java Development',
]

export default function SkillNexusPage() {
  const { modal, openModal, closeModal } = useModal()

  return (
    <>
      {/* ── PAGE HERO ── */}
      <section className={styles.hero} aria-label="SkillNexus hero">
        <div className={styles.heroBg} aria-hidden="true" />
        <div className={styles.heroContent}>
          <span
            className={styles.heroBadge}
            style={{ background: platform.colorBg, border: `1px solid ${platform.colorBorder}`, color: platform.color }}
          >
            <span className={styles.badgeDot} style={{ background: platform.dotColor }} />
            Upskilling Platform — Live now
          </span>
          <h1 className={styles.heroTitle}>
            <span className="grad">SkillNexus</span>
          </h1>
          <p className={styles.heroSub}>
            India's most structured AI-powered learning platform. Enrol in batches,
            learn day-by-day, and earn certificates recognized by top companies.
          </p>
          <div className={styles.heroActions}>
            <button className="btn btn-primary btn-lg" onClick={() => openModal('skillnexus')}>
              Explore SkillNexus
            </button>
            <a href="https://sn.nexusaiweb.com" target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-lg">
              Start Learning
            </a>
          </div>
        </div>
      </section>

      {/* ── PLATFORM CARD ── */}
      <section className={styles.cardSection} aria-label="SkillNexus details">
        <PlatformCard platform={platform} onOpenModal={openModal} />
      </section>

      {/* ── DEEP DIVE: overview, stats, pillars, journey, audience, FAQ ── */}
      <PlatformDetail id="skillnexus" />

      {/* ── FEATURES GRID ── */}
      <section className={styles.featuresSection} aria-label="SkillNexus features">
        <div className={styles.featuresInner}>
          <p className="section-label">Platform Features</p>
          <h2 className="section-title">Everything you need to upskill</h2>
          <div className={styles.featuresGrid}>
            {FEATURES_DETAIL.map(({ icon, title, desc, bg }) => (
              <div key={title} className={styles.featCard}>
                <div className={styles.featIcon} style={{ background: bg }}>{icon}</div>
                <h3 className={styles.featTitle}>{title}</h3>
                <p className={styles.featDesc}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COURSE GROUPS ── */}
      <section className={styles.coursesSection} aria-label="Available course groups">
        <div className={styles.coursesInner}>
          <p className="section-label">Curriculum</p>
          <h2 className="section-title">25+ Course Groups Available</h2>
          <p className="section-desc" style={{ marginBottom: 48 }}>
            From beginner to advanced — structured learning paths for every career goal.
          </p>
          <div className={styles.courseGrid}>
            {COURSE_GROUPS.map(c => (
              <div key={c} className={styles.courseChip}>{c}</div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS ── */}
      <StatsBand />

      {/* ── CTA ── */}
      <CtaSection
        label="Start Learning"
        title={'Enrol in your first batch\nstart learning today'}
        sub="Join thousands of learners building skills that get them hired."
        ctaText="Explore SkillNexus"
        ctaHref="https://sn.nexusaiweb.com"
      />

      {modal && <Modal type={modal} onClose={closeModal} />}
    </>
  )
}
